/** HarnessChangelog — the living-harness log (D-013), for people who own the course.
 *
 * WHY THIS IS COLLAPSED BY DEFAULT
 * --------------------------------
 * It sits on the course home page, which is the page a prospective buyer also
 * lands on. Expanded, a long log pushes the curriculum below the fold for the
 * one visitor who most needs to see it. Collapsed, it reads as a benefit that
 * exists — which is the job it does on this page. Reading happens after a click.
 *
 * The caller decides whether to mount this at all (it renders only for owners),
 * so there is no locked state here: an unentitled viewer never sees the section,
 * rather than seeing a teaser they cannot open. That is deliberate — the
 * changelog is a benefit of owning, not a lever for buying, and the access CTA
 * two sections up is already doing the selling.
 */
import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { Accordion, AccordionDetails, AccordionSummary, Box, Skeleton, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import { useTranslation } from "react-i18next";
import { getHarnessChangelog } from "../../api/client";
import LessonMarkdown from "../LessonMarkdown";

export default function HarnessChangelog() {
  const { t } = useTranslation("pages-core");
  const { data, isLoading } = useQuery({
    queryKey: ["harness-changelog"],
    queryFn: getHarnessChangelog,
  });

  // getHarnessChangelog swallows 403/404 into null. Rendering an empty shell
  // would tell an owner the log is broken during a bad deploy; rendering nothing
  // is the honest degradation, because the section is additive to this page.
  if (!isLoading && !data) return null;

  return (
    <Box sx={{ mt: 6 }}>
      <Accordion
        disableGutters
        elevation={0}
        sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, "&:before": { display: "none" } }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <HistoryOutlinedIcon fontSize="small" sx={{ mr: 1.5, color: "primary.main" }} />
          <Box>
            <Typography fontWeight={700}>{t("HarnessChangelog.title")}</Typography>
            <Typography variant="body2" color="text.secondary">
              {t("HarnessChangelog.subtitle")}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {isLoading ? (
            <Skeleton variant="rectangular" height={120} />
          ) : (
            <LessonMarkdown>{data as string}</LessonMarkdown>
          )}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}
